import { Print } from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import UNIVERSAL from "config";
import useFormatter from "hooks/useFormatter";
import { useRef } from "react";
import { FiPhone } from "react-icons/fi";
import { IoLocationOutline } from "react-icons/io5";
import { MdOutlineEmail } from "react-icons/md";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import { numberToWords } from "utils/numberToWord";

const PrintReceipt = ({ paymentData }) => {
  const { formatDate, formatCurrency } = useFormatter();
  const siteInfo = useSelector((state) => state?.base?.site_info);
  const printableContentRef = useRef();

  const ReceiptTemplate = ({ data, copyType }) => (
    <div className="receipt">
      {/* Watermark */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          opacity: 0.04,
          zIndex: 0,
          pointerEvents: "none",
          width: "50%",
        }}
      >
        <img
          src={UNIVERSAL.BASEURL + siteInfo?.site_logo}
          alt="Watermark"
          style={{
            width: "100%",
            height: "auto",
          }}
          className="watermark"
        />
      </Box>
      <div className="header">
        <div className="logo-section">
          {siteInfo?.site_logo ? (
            <img
              src={UNIVERSAL.BASEURL + siteInfo.site_logo}
              alt="Logo"
              style={{ maxHeight: "50px", maxWidth: "200px" }}
            />
          ) : (
            <div className="logo-text">
              <div className="logo-icon">E</div>
              <div>
                <div style={{ fontSize: "24px" }}>Education</div>
                <div
                  className="company-name"
                  style={{ fontSize: "12px", color: "#666" }}
                >
                  @
                </div>
              </div>
            </div>
          )}
          {/* <div className="copy-type">{copyType}</div> */}
          <Box
            sx={{
              backgroundColor: "#f0f0f0",
              px: 1.5,
              py: 0.5,
              borderRadius: "4px",
              width: "fit-content",
              mt: 1,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              textTransform="capitalize"
              sx={{
                color: copyType === "customer" ? "#2196f3" : "#ff6b35",
                fontSize: "11px",
              }}
            >
              {copyType === "customer" ? "Customer Copy" : "Office Copy"}
            </Typography>
          </Box>
        </div>

        <div className="office-info">
          <div className="office-info-row">
            <div className="ico-i">
              <IoLocationOutline />
            </div>
            <div className="office-label">Office :</div>
            {siteInfo?.site_address && (
              <div className="office-address">{siteInfo.site_address}</div>
            )}
          </div>

          {siteInfo?.phone && (
            <div className="contact-info">
              <div className="ico-i">
                <FiPhone />
              </div>
              <span className="contact-number">{siteInfo.phone}</span>
            </div>
          )}

          {siteInfo?.email_address && (
            <div className="email-info">
              <div className="ico-i">
                <MdOutlineEmail />
              </div>
              <span className="email-address">{siteInfo.email_address}</span>
            </div>
          )}
        </div>
      </div>
      {/* <div className="copy-type">({copyType})</div> */}

      <div className="receipt-no">
        <div>
          <span className="label">No. </span>
          <span className="value">{data?.invoice_number || "18553"}</span>
        </div>
        <div className="money-receipt-badge">MONEY RECEIPT</div>
        <div>
          <span className="label">Date: </span>
          <span className="value">
            {data?.payment_date ? formatDate(data.payment_date) : "___________"}
          </span>
        </div>
      </div>

      <div className="form-row">
        <div className="form-label">Received with thanks from Mr / Ms. :</div>
        <div className="form-value">{data?.customer_name || ""}</div>
      </div>

      <div className="form-row">
        <div className="form-label">Address :</div>
        <div className="form-value">{data?.customer_city || ""}</div>
      </div>

      <div className="form-row">
        <div className="form-label">The sum of taka (in words) :</div>
        <div className="form-value">
          {data?.amount ? numberToWords(data.amount) : ""}
        </div>
      </div>

      <div className="form-row">
        <div className="form-label">In Cash / Cheque / Mobile Banking :</div>
        <div className="form-value">{data?.payment_method_name || ""}</div>
      </div>

      <div className="form-row">
        <div className="form-label">Purpose of Payment :</div>
        <div className="form-value">
          {data?.purpose?.length
            ? Array.isArray(data.purpose)
              ? data.purpose.join(", ")
              : data.purpose
            : ""}
        </div>
        {/* <div className="form-label">in full/part of installment.</div> */}
      </div>

      <div className="footer">
        <div className="amount-wrapper">
          <span className="tk-label">Tk.</span>
          <div className="amount-box">
            {data?.amount
              ? formatCurrency(data.amount, data?.symbol) + "/-"
              : ""}
          </div>
        </div>
        <div className="signature-box">
          <div className="signature-line">Accounts Chief</div>
        </div>
        <div className="signature-box">
          <div className="signature-line">Authorised</div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div style={{ display: "none" }}>
        <div ref={printableContentRef} className="print-container">
          <div className="receipt-section">
            <ReceiptTemplate data={paymentData} copyType="customer" />
          </div>
          <div className="cutoff-line"></div>
          <div className="receipt-section">
            <ReceiptTemplate data={paymentData} copyType="office" />
          </div>
        </div>
      </div>

      <ReactToPrint
        documentTitle={paymentData?.invoice_number || "Money Receipt"}
        trigger={() => (
          <Tooltip title="Print">
            <IconButton>
              <Print />
            </IconButton>
          </Tooltip>
        )}
        content={() => printableContentRef.current}
        pageStyle="@page { size: A4; margin: 0; } @media print { body { margin: 20mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }"
      />

      <style>
        {`
          @media print {
            @page {
              size: A4;
              margin: 0;
            }
            body {
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              color-adjust: exact;
            }
          }
          
          .print-container {
            display: flex;
            flex-direction: column;
            height: 100vh;
            min-height: 297mm;
            padding: 0;
            margin: 0;
          }
          
          @media print {
            .print-container {
              height: 297mm;
              min-height: 297mm;
              padding: 0;
              margin: 0;
            }
          }
          
          .receipt-section {
            height: calc(50% - 10px);
            display: flex;
            align-items: flex-start;
            justify-content: center;
            padding: 10mm;
            box-sizing: border-box;
            position: relative;
          }
          
          @media print {
            .receipt-section {
              height: calc(148.5mm - 10px);
              padding: 15mm;
              box-sizing: border-box;
            }
          }
          
          .cutoff-line {
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            flex-shrink: 0;
          }
          
          .cutoff-line::before {
            content: "✂ - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -- - - - - - - -";
            color: #999;
            font-size: 14px;
            letter-spacing: 2px;
            text-align: center;
            width: 100%;
          }
          
          @media print {
            .cutoff-line::before {
              color: #999;
            }
          }
          
          .receipt {
            padding: 15px;
            background: white;
            page-break-inside: avoid;
            overflow: hidden;
            width: 100%;
            max-width: 800px;
          }
          
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
          }
          
          .logo-section {
            flex: 1;
          }
          
          .logo-section img {
            max-height: 50px;
            max-width: 200px;
          }
          
          .copy-type {
            margin-top: 8px;
            font-size: 12px;
            font-weight: 600;
            color: #666;
            text-align: left;
          }
          
          .logo-text {
            font-size: 28px;
            font-weight: bold;
            color: #c41e3a;
            display: flex;
            align-items: center;
            gap: 5px;
          }
          
          .logo-icon {
            width: 40px;
            height: 40px;
            background: linear-gradient(135deg, #c41e3a 0%, #8b1a2d 100%);
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 24px;
            font-weight: bold;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .money-receipt-badge {
            background: #0eb9ab;
            color: #fff;
            padding: 6px 18px;
            border-radius: 20px;
            font-weight: bold;
            font-size: 14px;
            white-space: nowrap;
            letter-spacing: 0.9px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .office-info {
            max-width: 40%;
            text-align: left;
            font-size: 11px;
            color: #666;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
          }

          .office-info-row {
            display: flex;
            align-items: flex-start;
            flex-wrap: wrap;
          }

          .office-info-row .ico-i {
            margin-top: 3px;
          }

          .ico-i{
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 12px;
            height: 12px;
            margin-right: 4px;
            color: #fff;
            background-color: #0eb9ab;
            border-radius: 50%;
            padding: 2px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .office-label {
            color: #000;
            font-weight: bold;
            font-size: 11px;
            display: inline-flex;
            align-items: center;
            gap: 5px;
            padding-top: 1px;
          }
          
          .contact-info {
            font-weight: normal;
            display: flex;
            align-items: flex-start;
          }

          .contact-info .ico-i {
            margin-top: 2px;
          }
          
          .contact-number {
            font-weight: bold;
          }
          
          .email-info {
            font-weight: normal;
            display: flex;
            align-items: flex-start;
          }
          .email-info .ico-i {
            margin-top: 2px;
          }
          
          .email-address {
            font-weight: bold;
          }
          
          .receipt-no {
            display: flex;
            justify-content: space-between;
            align-items: end;
            margin-bottom: 20px;
            font-size: 13px;
          }
          
          @media print {
            .receipt-no {
              margin-bottom: 10px;
            }
          }
          
          .receipt-no .label {
            font-weight: normal;
            color: #333;
          }
          
          .receipt-no .value {
            font-weight: bold;
            font-size: 16px;
            color: #000;
          }
          
          .form-row {
            display: flex;
            gap: 10px;
            margin-bottom: 10px;
            align-items: baseline;
            padding-bottom: 3px;
          }
          
          @media print {
            .form-row {
              margin-bottom: 6px;
            }
          }
          
          .form-label {
            font-size: 12px;
            color: #333;
          }
          
          .form-value {
            flex: 1;
            font-size: 12px;
            // color: #666;
            font-weight: 500;
            border-bottom: 1px dotted #999;
            min-height: 16px;
          }
          
          .footer {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin-top: 20px;
            padding-top: 20px;
          }
          
          @media print {
            .footer {
              margin-top: 10px;
              padding-top: 10px;
            }
          }
          
          .amount-wrapper {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          .tk-label {
            font-size: 16px;
            font-weight: bold;
            color: #000;
          }
          
          .amount-box {
            border: 1px solid #a8e6e3;
            background: #a8e6e350;
            padding: 10px 15px;
            border-radius: 3px;
            display: inline-block;
            min-width: 150px;
            min-height: 44px;
            font-size: 16px;
            font-weight: bold;
            color: #000;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .signature-box {
            text-align: center;
            min-width: 150px;
          }
          
          .signature-line {
            border-top: 1px solid #000;
            margin-top: 35px;
            padding-top: 3px;
            font-size: 11px;
            color: #666;
          }
        `}
      </style>
    </>
  );
};

export default PrintReceipt;
